import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, SvgIcon } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentCaptions } from "@fortawesome/pro-regular-svg-icons";
import apiClient from "@services/api/apiClient";
import API from "@config/shared/api-paths";
import { BottomBarContext } from "@context/client/BottomBar";
import { FeedbackDialog } from "./feedback-dialog";

// Approximate height of the fixed footer bars (e.g. SubmitTabs) that can
// appear at the bottom of a page — clears the FAB above them when visible.
const BOTTOM_BAR_CLEARANCE = 72;

export const FeedbackButton = () => {
  const { t } = useTranslation();
  const { isBottomBarVisible } = useContext(BottomBarContext);
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    setOpen(false);
    setFeedback("");
    setSubmitted(false);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!feedback.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(API.FRONTEND.FEEDBACKS, {
        message: feedback,
        pageUrl: window.location.pathname,
      });
      setSubmitted(true);
      setFeedback("");
    } catch {
      setError(t("common.feedbackError"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <FeedbackDialog
        open={open}
        onClose={handleClose}
        feedback={feedback}
        onFeedbackChange={setFeedback}
        submitted={submitted}
        submitting={submitting}
        error={error}
        onSubmit={handleSubmit}
      />

      {/* FAB — fixed bottom-right, expands on hover */}
      <Box
        sx={{
          position: "fixed",
          bottom: {
            xs: 16 + (isBottomBarVisible ? BOTTOM_BAR_CLEARANCE : 0),
            md: 24 + (isBottomBarVisible ? BOTTOM_BAR_CLEARANCE : 0),
          },
          right: { xs: 16, md: 24 },
          transition: "bottom 0.2s ease",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Button
          variant="contained"
          onClick={() => setOpen((v) => !v)}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          sx={{
            borderRadius: "50px",
            height: { xs: 44, md: 52 },
            minWidth: { xs: 44, md: 52 },
            px: hovered ? 2.5 : 0,
            transition: "padding 0.3s ease",
            bgcolor: (t) => t.palette.brand2,
            "&:hover": {
              bgcolor: (t) => t.palette.brand2,
              filter: "brightness(1.15)",
            },
            overflow: "hidden",
          }}
        >
          <SvgIcon
            sx={{ flexShrink: 0, fontSize: { xs: "1.1rem", md: "1.25rem" } }}
          >
            <FontAwesomeIcon icon={faCommentCaptions} />
          </SvgIcon>
          <Box
            sx={{
              maxWidth: hovered ? "160px" : 0,
              opacity: hovered ? 1 : 0,
              overflow: "hidden",
              whiteSpace: "nowrap",
              ml: hovered ? 1 : 0,
              transition:
                "max-width 0.3s ease, opacity 0.2s ease, margin 0.3s ease",
              fontSize: { xs: "0.75rem", md: "0.875rem" },
            }}
          >
            {t("common.feedbackWrite")}
          </Box>
        </Button>
      </Box>
    </>
  );
};
